import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Download } from 'lucide-react';
import orderService from '../../services/orderService';

/**
 * Persistent (non-dismissable) admin alerts for scheduled-order pick lists whose
 * slot cutoff has passed and are ready to download. Shown in the Dashboard,
 * Orders and Delivery Slots sections. Self-contained: fetches on mount and polls
 * every 60s; renders nothing when no slot is ready.
 */
const formatDate = (ymd) => {
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const ScheduledListAlerts = () => {
  const [lists, setLists] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const fetchLists = useCallback(async () => {
    const res = await orderService.getReadyScheduledLists();
    if (res.success) setLists(res.data);
  }, []);

  useEffect(() => {
    fetchLists();
    const id = setInterval(fetchLists, 60000);
    return () => clearInterval(id);
  }, [fetchLists]);

  if (!lists.length) return null;

  const handleDownload = async (item) => {
    const { date, slot, label } = item;
    const key = `${date}__${slot}`;
    setDownloading(key);
    
    try {
      const res = await orderService.downloadScheduledList({ date, slot, label });
      if (!res.success) alert(res.error || 'Failed to download list');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-3 mb-6">
      {lists.map((item) => {
        const key = `${item.date}__${item.slot}`;
        const busy = downloading === key;
        return (
          <div
            key={key}
            className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3"
          >
            <ClipboardList className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                Scheduled order list ready to download
              </p>
              <p className="text-sm text-amber-800">
                {formatDate(item.date)} · {item.slot} — {item.orderCount} order{item.orderCount === 1 ? '' : 's'}
              </p>
            </div>
            <button
              onClick={() => handleDownload(item)}
              disabled={busy}
              className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 shrink-0"
            >
              {busy ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download List
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ScheduledListAlerts;
